<script setup lang="ts">
import BaseModal from '@/components/modal/BaseModal.vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import { useModalStore } from '@/stores/modal';
import { nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';

interface PolicySection {
  title?: string;
  start: number;
  items: string[];
}

interface Props {
  isOpen: boolean;
  title: string;
  heading: string;
  description: string;
  sections: PolicySection[];
  notes?: string[];
  onConfirm: () => void;
}

const props = defineProps<Props>();
const modalStore = useModalStore();
const contentRef = ref<HTMLElement | null>(null);
const scrollHostRef = ref<HTMLElement | null>(null);
const hasReachedBottom = ref(false);

const findScrollHost = () => {
  const host = contentRef.value?.parentElement;
  scrollHostRef.value = host ?? null;
};

const updateScrollState = () => {
  const el = scrollHostRef.value;
  if (!el) return;

  hasReachedBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
};

const handleScroll = () => {
  updateScrollState();
};

const detachScrollListener = () => {
  scrollHostRef.value?.removeEventListener('scroll', handleScroll);
};

const attachScrollListener = () => {
  detachScrollListener();
  scrollHostRef.value?.addEventListener('scroll', handleScroll, { passive: true });
};

const resetScrollState = async () => {
  await nextTick();
  findScrollHost();
  attachScrollListener();

  const el = scrollHostRef.value;
  if (!el) return;

  el.scrollTop = 0;
  hasReachedBottom.value = false;

  requestAnimationFrame(() => {
    updateScrollState();
  });
};

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      resetScrollState();
    } else {
      detachScrollListener();
    }
  },
);

onMounted(() => {
  if (props.isOpen) {
    resetScrollState();
  }
});

onBeforeUnmount(() => {
  detachScrollListener();
});

const handleClose = () => {
  modalStore.close();
};

const handleConfirm = () => {
  if (!hasReachedBottom.value) return;

  props.onConfirm();
  modalStore.close();
};
</script>

<template>
  <BaseModal :title="title" width="560px" aria-label="필수 확인 문서" @close="handleClose">
    <div ref="contentRef" class="space-y-[20px] px-1 py-1 sm:px-2">
      <div class="rounded-[18px] border border-[#f1dfd2] bg-[#fff4eb] px-5 py-5 text-[14px] leading-[1.65] text-[#8c5a3a] sm:px-6 sm:py-5 sm:text-[15px]">
        <p class="font-[800] text-[#d56a2f]">필수 확인 문서</p>
        <p class="mt-2">{{ description }}</p>
        <div class="mt-3 rounded-[14px] border border-[#f3d2be] bg-white px-4 py-4 font-[800] text-[#8c5a3a] sm:px-5">
          문서 하단까지 스크롤하면 <span class="text-[#d56a2f]">모두 확인함</span> 버튼이 활성화됩니다.
        </div>
      </div>

      <section class="px-2 pb-1 pt-1 text-[15px] leading-[1.8] text-[#4d3e34] sm:px-3 sm:text-[16px]">
        <h3 class="text-[18px] font-[900] leading-[1.5] text-[#2f241c] sm:text-[20px]">{{ heading }}</h3>

        <div class="mt-5 space-y-5">
          <div v-for="section in sections" :key="section.title || section.start">
            <p v-if="section.title" class="mb-3 text-[15px] font-[900] text-[#8b5b39] sm:text-[16px]">{{ section.title }}</p>
            <ol class="space-y-3">
              <li
                v-for="(item, index) in section.items"
                :key="`${section.title || section.start}-${index}`"
                class="grid grid-cols-[24px_minmax(0,1fr)] gap-2.5"
              >
                <span class="text-[15px] font-[900] leading-[1.75] text-[#d56a2f] sm:text-[16px]">{{ section.start + index }}.</span>
                <span>{{ item }}</span>
              </li>
            </ol>
          </div>
        </div>

        <div v-if="notes?.length" class="mt-6 space-y-2 text-[14px] leading-[1.75] text-[#2954a3] sm:text-[15px]">
          <p v-for="note in notes" :key="note" class="break-keep">{{ note }}</p>
        </div>
      </section>

      <p class="px-2 text-[14px] leading-[1.6] text-[#7b675b] sm:px-3 sm:text-[15px]">
        {{ hasReachedBottom ? '끝까지 읽었습니다. 확인 버튼을 눌러 반영해주세요.' : '문서 하단까지 읽으면 버튼이 활성화됩니다.' }}
      </p>
    </div>

    <template #footer>
      <ModalFooterConfirmButtons
        cancel-text="닫기"
        confirm-text="모두 확인함"
        :disabled="!hasReachedBottom"
        @cancel="handleClose"
        @confirm="handleConfirm"
      />
    </template>
  </BaseModal>
</template>
